import {
  createRootRoute,
  Outlet,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import type { FC } from "react";
import { lazy, useEffect } from "react";
import { BackToTop } from "@/components/BackToTop";
import { LoadingIcon } from "@/components/Icons";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ToastContextProvider } from "@/context/ToastContext";
import { TopNavProvider } from "@/context/TopNavContext";
import { TopNav } from "@/layout/TopNav";

// Import Tailwind CSS and custom styles
import "@/styles/style.css";

/**
 * Lazy-load devtools ONLY in development.
 * This conditional dynamic import ensures 0 bytes in production bundle.
 */
const TanStackDevtoolsLazy = import.meta.env.DEV
  ? lazy(() =>
      import("@/devtools").then((m) => ({ default: m.TanStackDevtools })),
    )
  : null;

// Loading component
export const LoadingScreen = () => (
  <div
    className="flex h-screen min-h-[var(--loading-screen-min-height)] w-full flex-col items-center justify-center text-center"
    data-testid="loading-screen"
  >
    <LoadingIcon
      svgClassName="h-5 w-5 mb-3 text-blue-400"
      wrapperClassName="flex items-center justify-center"
    />
    Loading
  </div>
);

// 404 Not Found component
const NotFoundComponent: FC = () => {
  const router = useRouter();

  return (
    <div className="flex flex-1 flex-col items-center justify-center p-4">
      <h1 className="text-6xl font-bold text-theme-primary mb-4">404</h1>
      <p className="text-xl text-theme-secondary mb-8">Page not found</p>
      <button
        className="px-6 py-3 bg-theme-primary text-white rounded-md hover:bg-theme-primary/90 active:bg-theme-primary/80 transition-colors-standard font-medium cursor-pointer"
        onClick={() => router.navigate({ to: "/" })}
        type="button"
      >
        Back to Home
      </button>
    </div>
  );
};

NotFoundComponent.displayName = "NotFoundComponent";

const SeoSync = () => {
  const _location = useRouterState({ select: (state) => state.location });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__syncSeoTags__?.();
  }, []);

  return null;
};

// Root layout component
const RootComponent = () => {
  const router = useRouter();

  return (
    <ToastContextProvider>
      <TopNavProvider>
        <SeoSync />
        <div className="h-dvh overflow-hidden flex flex-col">
          <TopNav />
          <div className="flex-1 min-h-0 overflow-y-auto">
            <Outlet />
          </div>
          <BackToTop />
          {TanStackDevtoolsLazy && <TanStackDevtoolsLazy router={router} />}
        </div>
      </TopNavProvider>
    </ToastContextProvider>
  );
};

// Create root route
export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFoundComponent,
});
